import { toApiUrl } from '../utils/apiBase';
import { receivingBankOptions, paymentAccountDetails } from '../utils/receivingBanks';
import React, { useState, useContext, useEffect, useRef } from 'react';
import firebase from 'firebase/compat/app';
import 'firebase/compat/firestore';
import 'firebase/compat/storage';
import firestore from '../firestore';
import Firebase from '../firebase_setup';
import { GlobalContext } from '../context/GlobalContext';
import { ContainerSpinner } from '../components/Spinner';
import Spinner from '../components/Spinner';
import Select from '../components/Select';
import { ucfirst, format_identification_with_prefix } from '../utils/formatters';
import { Dialog } from '../components/Dialog';
import { DatePicker } from '../shadcn/DatePicker';
import Stepper from '../components/Stepper';
import { Button } from '../shadcn/Button';
import { Checkbox } from '../shadcn/Checkbox';
import Input from '../components/Input';
import {
	AlertTriangle,
	ArrowLeft,
	CheckCircle,
	ChevronDown,
	ChevronUp,
	List,
	Search,
	Wallet,
	CircleCheck,
	Upload,
	BadgePercent,
	ShieldCheck,
	KeyRound,
	Edit,
	Printer
} from 'lucide-react';
import Notice from '../components/Notice';
import classNames from 'classnames';
import PhoneInput from '../components/PhoneInput';
import { validate_phone_number } from '../utils/validate';
import { callAPI } from '../hooks/api';
import { TwoFactorAuthV2 } from '../components/2FA';
import { getActiveImpersonationSession, getImpersonationHeaders } from '../utils/impersonation';

// Preserve Firestore orderBy(dueDate), including nanoseconds and document-name ties.
const pendingPayments = (items) => items.filter((item) => !item.paid && item.is_active !== false);

const remainingAmount = (payment) => Math.max(0, Number(payment.actualAmount) - Number(payment.amountPaid || 0));
const hasPendingReview = (payment) => (payment.paid && !payment.checked_by_internal_team) ||
	(Array.isArray(payment.unverified_payment) ? payment.unverified_payment.length > 0 : Boolean(payment.unverified_payment));
const paymentReceiver = (payment) => {
	const store = payment.related_store?.id || payment.related_store;
	// Mirror payments.payment_targets.payment_receiver_id; the backend validates this again.
	return payment.store_financed || store === 'sparktechkrece@gmail.com' ? store : 'default';
};
const selectionTotal = (items) => Math.round(items.reduce((sum, item) => sum + remainingAmount(item), 0) * 100) / 100;
const RECEIVER_CHANGED_MESSAGE = 'Los datos del destinatario cambiaron. Revísalos y selecciona nuevamente el método de pago.';

// Compare recipient values in a fixed order, not Firestore map order or unrelated configuration.
const paymentMethodsSignature = (data) => JSON.stringify([
	['pagomovil', ['bank', 'phoneNumber', 'rif']],
	['transferBs', ['bank', 'account', 'rif', 'titular', 'type']],
	['transferUsd', ['bank', 'account', 'rif', 'titular', 'type']]
].map(([method, fields]) => fields.map((field) => data?.[method]?.[field] ?? '')));

// Match the app: selecting a later cuota includes earlier debt; removing it removes later debt.
const toggleInstallmentIds = (selected, items, id) => {
	const unpaid = pendingPayments(items);
	const index = unpaid.findIndex((item) => item.id === id);
	if (index < 0) return selected;
	const next = new Set(selected);
	if (next.has(id)) unpaid.slice(index).forEach((item) => next.delete(item.id));
	else unpaid.slice(0, index + 1).forEach((item) => next.add(item.id));
	return [...next];
};

const Pay = () => {
	const { authenticated, userData, internal, internalCountry, CountrySettings } = useContext(GlobalContext);
	const impersonationHeaders = getImpersonationHeaders(getActiveImpersonationSession(userData?.email));
	const uidPrefix = CountrySettings?.uid_prefix || '';

	const isInactive = userData?.is_active === false || userData?.master_store?.is_active === false;

	const [isLoading, setIsLoading] = useState(false);
	const [errorMessage, setErrorMessage] = useState('');
	const [success, setSuccess] = useState(false);
	const [storePaymentReceipt, setStorePaymentReceipt] = useState(null);

	const country = internalCountry.get() || userData?.country;
	// Bolivia collects through Libelula, not cash received by the store.
	const isPaymentBlocked = isInactive && !userData?.self_financed && country !== 'bolivia';
	const banks = CountrySettings?.banks?.toSorted() || [];

	const [page, setPage] = useState(1);
	const [cedula, setCedula] = useState('');
	const [payments, setPayments] = useState([]);
	const [selectionMode, setSelectionMode] = useState(false);
	const [selectedInstallmentIds, setSelectedInstallmentIds] = useState([]);
	const [combinedPaymentIds, setCombinedPaymentIds] = useState([]);
	const paymentsUnsubscribe = useRef(null);
	const searchGeneration = useRef(0);
	const snapshotRevision = useRef(0);
	const preparationRevision = useRef(0);
	const preparationPending = useRef(false);
	const preparedSelection = useRef(null);
	const receiverUnsubscribe = useRef(null);
	const selectedInstallments = Object.values(payments).flatMap((group) => group.payments)
		.filter((payment) => selectedInstallmentIds.includes(payment.id));
	const selectedTotal = selectionTotal(selectedInstallments);
	const [email, setEmail] = useState('');
	const [token, setToken] = useState(process.env.REACT_APP_API_TOKEN);
	const [showPayments, setShowPayments] = useState({});
	const [selectedPaymentId, setSelectedPaymentId] = useState('');
	const [paymentDocId, setPaymentDocId] = useState('');
	const paymentTargetPayload = combinedPaymentIds.length > 1
		? { payment_object_ids: combinedPaymentIds } : { payment_object_id: paymentDocId };
	const [amountToPayDollars, setAmountToPayDollars] = useState(0);
	const [exchangeRate, setExchangeRate] = useState('');
	const [payMethod, setPayMethod] = useState('');
	const [confirmationNumber, setConfirmationNumber] = useState('');
	const [amountPaid, setAmountPaid] = useState('');
	const [clientName, setClientName] = useState('');
	const [paymentDate, setPaymentDate] = useState('');
	const [phoneNumber, setPhoneNumber] = useState('');
	const [bank, setBank] = useState('');
	const [paymentReceiverId, setPaymentReceiver] = useState(null);
	const usesCountryPaymentDetails = country === 'venezuela' && paymentReceiverId === 'default';
	const [verificationBank, setVerificationBank] = useState('auto');
	const receiverPaymentType = payMethod === 'pagomovil' ? 'p2c' : 'transfer';
	const configuredReceiverAccounts = paymentAccountDetails(CountrySettings, receiverPaymentType);
	const receiverOptions = receivingBankOptions(CountrySettings, receiverPaymentType)
		.filter((option) => configuredReceiverAccounts.some((account) => account.api_type === option.value));
	const selectedReceiver = usesCountryPaymentDetails && verificationBank === 'auto' ? receiverOptions[0]?.value : verificationBank;
	const receiverAvailable = usesCountryPaymentDetails
		? receiverOptions.some((option) => option.value === selectedReceiver)
		: verificationBank === 'auto';
	useEffect(() => {
		setVerificationBank('auto');
	}, [country, payMethod, paymentReceiverId]);
	const [issuerCustomerId, setIssuerCustomerId] = useState('');
	const [accountNumber, setAccountNumber] = useState('');
	const [accountHolder, setAccountHolder] = useState('');
	const [paymentProof, setPaymentProof] = useState(null);
	const [uploadingProof, setUploadingProof] = useState(false);
	const [pagoMovilData, setPagoMovilData] = useState({});
	const [transferData, setTransferData] = useState({});
	const [transferDataUsd, setTransferDataUsd] = useState({});
	const pagoMovilAccounts = usesCountryPaymentDetails ? paymentAccountDetails(CountrySettings, 'p2c') : (pagoMovilData?.bank ? [pagoMovilData] : []);
	const transferAccounts = usesCountryPaymentDetails ? paymentAccountDetails(CountrySettings, 'transfer') : (transferData?.account ? [transferData] : []);
	const transferUsdAccounts = usesCountryPaymentDetails ? paymentAccountDetails(CountrySettings, 'transfer_usd') : (transferDataUsd?.account ? [transferDataUsd] : []);

	const [masterStorePayment2fa, setMasterStorePayment2fa] = useState({});
	const [amountReceived, setAmountReceived] = useState('');
	const [onlinePaymentAmount, setOnlinePaymentAmount] = useState('');
	const [subStore, setSubStore] = useState('');
	const [subStoreList, setSubStoreList] = useState([]);
	const [storePayment2faSelectedMethod, setStorePayment2faSelectedMethod] = useState(null);
	const [storePayment2faCodeSent, setStorePayment2faCodeSent] = useState(false);
	const [storePayment2faCodeInput, setStorePayment2faCodeInput] = useState('');
	const [storePayment2fa, setStorePayment2fa] = useState(null);
	const [showStore2faPhoneEditor, setShowStore2faPhoneEditor] = useState(false);
	const [store2faPhoneValue, setStore2faPhoneValue] = useState('');
	const [store2faPhoneCodeSent, setStore2faPhoneCodeSent] = useState(false);
	const [store2faPhoneCode, setStore2faPhoneCode] = useState('');
	const [store2faPhoneLoading, setStore2faPhoneLoading] = useState(false);
	const [store2faPhoneMessage, setStore2faPhoneMessage] = useState('');
	const [store2faPhoneError, setStore2faPhoneError] = useState('');
	const [dialogMessage, setDialogMessage] = useState('');
	const [showDialog, setShowDialog] = useState(false);
	const [showConfirmDialog, setShowConfirmDialog] = useState(false);
	const [pendingConfirmAction, setPendingConfirmAction] = useState(null);
	const [prontoPagoNotice, setProntoPagoNotice] = useState({});
	const [prontoPagoAmount, setProntoPagoAmount] = useState(null);
	const [loadingProntoPago, setLoadingProntoPago] = useState({});

	const isEarlyPayment = prontoPagoNotice?.type === 'success';

	function resetAll() {
		searchGeneration.current += 1;
		preparationRevision.current += 1;
		preparationPending.current = false;
		preparedSelection.current = null;
		receiverUnsubscribe.current?.();
		receiverUnsubscribe.current = null;
		paymentsUnsubscribe.current?.();
		paymentsUnsubscribe.current = null;
		setSelectionMode(false);
		setSelectedInstallmentIds([]);
		setCombinedPaymentIds([]);
		setIsLoading(false);
		setErrorMessage('');
		setPage(1);
		setCedula('');
		setPayments([]);
		setEmail('');
		setToken(process.env.REACT_APP_API_TOKEN);
		setShowPayments({});
		setSelectedPaymentId('');
		setPaymentDocId('');
		setAmountToPayDollars(0);
		setExchangeRate('');
		setPayMethod('');
		setConfirmationNumber('');
		setAmountPaid('');
		setClientName('');
		setPaymentDate('');
		setPhoneNumber('');
		setBank('');
		setVerificationBank('auto');
		setPaymentReceiver(null);
		setIssuerCustomerId('');
		setAccountNumber('');
		setAccountHolder('');
		setPaymentProof(null);
		setUploadingProof(false);
		setPagoMovilData({});
		setTransferData({});
		setTransferDataUsd({});
		setMasterStorePayment2fa({});
		setAmountReceived('');
		setSubStore('');
		setSubStoreList([]);
		setStorePayment2faSelectedMethod(null);
		setStorePayment2faCodeSent(false);
		setStorePayment2faCodeInput('');
		setStorePayment2fa(null);
		setShowStore2faPhoneEditor(false);
		setStore2faPhoneValue('');
		setStore2faPhoneCodeSent(false);
		setStore2faPhoneCode('');
		setStore2faPhoneLoading(false);
		setStore2faPhoneMessage('');
		setStore2faPhoneError('');
		setDialogMessage('');
		setShowDialog(false);
		setShowConfirmDialog(false);
		setPendingConfirmAction(null);
		setProntoPagoAmount(null);
		setProntoPagoNotice({});
		setLoadingProntoPago({});
		setSuccess(false);
		setStorePaymentReceipt(null);
		setOnlinePaymentAmount('');
	}

	useEffect(() => {
		resetAll();
		return () => {
			searchGeneration.current += 1;
			preparationRevision.current += 1;
			receiverUnsubscribe.current?.();
			paymentsUnsubscribe.current?.();
		};
		// A country's selection and receiver must never leak into the next country.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [country]);

	const toggleInstallment = (payment, paymentList) => {
		const ids = toggleInstallmentIds(selectedInstallmentIds, paymentList, payment.id);
		const chosen = Object.values(payments).flatMap((group) => group.payments)
			.filter((item) => ids.includes(item.id));
		if (!selectedInstallmentIds.includes(payment.id)) {
			if (chosen.some((item) => !Number.isFinite(remainingAmount(item)) || remainingAmount(item) <= 0)) {
				showAlert('Una cuota ya no tiene saldo disponible. Actualiza la búsqueda.');
				return;
			}
			if (ids.length > 50) {
				showAlert('Puedes seleccionar hasta 50 cuotas por pago.');
				return;
			}
			if (new Set(chosen.map(paymentReceiver)).size > 1) {
				showAlert('Estas compras tienen destinatarios de pago distintos. Repórtalas por separado.');
				return;
			}
			const cap = Number(CountrySettings.max_payment_allowed);
			if (country === 'venezuela' && cap > 0 && selectionTotal(chosen) > cap) {
				showAlert(`Puedes combinar hasta ${CountrySettings.currency_code}${formatNumber(cap)} por pago.`);
				return;
			}
		}
		setSelectedInstallmentIds(ids);
	};

	const formatNumber = (number) => {
		return new Intl.NumberFormat('en-US', {
			style: 'decimal',
			minimumFractionDigits: 2,
			maximumFractionDigits: 2
		}).format(number);
	};

	const showAlert = (message) => {
		setDialogMessage(message);
		setShowDialog(true);
	};

	const formatReceiptDate = (paidAt) =>
		new Intl.DateTimeFormat('es', {
			dateStyle: 'long',
			timeStyle: 'short'
		}).format(new Date(paidAt));

	const printStorePaymentReceipt = () => {
		if (!storePaymentReceipt) return;

		const printWindow = window.open('', '_blank', 'width=520,height=760');
		if (!printWindow) {
			showAlert('Habilite las ventanas emergentes para imprimir o guardar el comprobante.');
			return;
		}

		const escapeHtml = (value) =>
			String(value ?? '')
				.replace(/&/g, '&amp;')
				.replace(/</g, '&lt;')
				.replace(/>/g, '&gt;')
				.replace(/"/g, '&quot;')
				.replace(/'/g, '&#039;');
		const receipt = storePaymentReceipt;
		const amount = `${receipt.currencyCode}${formatNumber(receipt.amount)}`;
		const branchRow = receipt.branchName
			? `<div class="row"><span>Local</span><strong>${escapeHtml(receipt.branchName)}</strong></div>`
			: '';

		printWindow.document.write(`<!doctype html>
			<html lang="es">
				<head>
					<meta charset="utf-8" />
					<meta name="viewport" content="width=device-width, initial-scale=1" />
					<title>Comprobante ${escapeHtml(receipt.confirmationCode)}</title>
					<style>
						@page { margin: 12mm; }
						* { box-sizing: border-box; }
						body { margin: 0; color: #171717; font-family: Arial, sans-serif; }
						.receipt { width: 100%; max-width: 420px; margin: 0 auto; }
						.brand { color: #087f86; font-size: 24px; font-weight: 800; letter-spacing: -1px; }
						h1 { margin: 20px 0 4px; font-size: 20px; }
						.subtitle { margin: 0 0 24px; color: #525252; font-size: 13px; }
						.amount { margin-bottom: 20px; border: 1px solid #d4d4d4; border-radius: 10px; padding: 16px; }
						.amount span { display: block; color: #525252; font-size: 12px; }
						.amount strong { display: block; margin-top: 4px; font-size: 28px; }
						.details { border-top: 1px solid #d4d4d4; }
						.row { display: flex; justify-content: space-between; gap: 20px; border-bottom: 1px solid #e5e5e5; padding: 10px 0; font-size: 13px; }
						.row span { color: #525252; }
						.row strong { max-width: 65%; text-align: right; overflow-wrap: anywhere; }
						.footer { margin-top: 24px; color: #525252; font-size: 11px; line-height: 1.5; text-align: center; }
					</style>
				</head>
				<body>
					<main class="receipt">
						<div class="brand">Krece</div>
						<h1>Comprobante de pago</h1>
						<p class="subtitle">Pago recibido en tienda</p>
						<div class="amount"><span>Monto recibido</span><strong>${escapeHtml(amount)}</strong></div>
						<div class="details">
							<div class="row"><span>Cliente</span><strong>${escapeHtml(receipt.clientName)}</strong></div>
							<div class="row"><span>${escapeHtml(receipt.identificationLabel)}</span><strong>${escapeHtml(receipt.identificationNumber)}</strong></div>
							<div class="row"><span>Tienda</span><strong>${escapeHtml(receipt.storeName)}</strong></div>
							${branchRow}
							<div class="row"><span>Fecha</span><strong>${escapeHtml(formatReceiptDate(receipt.paidAt))}</strong></div>
							<div class="row"><span>Confirmación</span><strong>${escapeHtml(receipt.confirmationCode)}</strong></div>
						</div>
						<p class="footer">Este comprobante confirma la recepción del pago en tienda.</p>
					</main>
				</body>
			</html>`);
		printWindow.document.close();
		printWindow.focus();
		printWindow.print();
	};

	const showConfirm = (message, onConfirm) => {
		setDialogMessage(message);
		setPendingConfirmAction(() => onConfirm);
		setShowConfirmDialog(true);
	};

	useEffect(() => {
		if (success) {
			setPage(4);
		} else if (paymentDocId && exchangeRate) {
			setPage(3);
		} else if (payments && Object.keys(payments).length > 0) {
			setPage(2);
		} else {
			setPage(1);
		}
	}, [success, paymentDocId, exchangeRate, payments]);

	useEffect(() => {
		if (!authenticated) return;

		try {
			if (!userData.master_user) {
				setSubStoreList([userData.store_name]);
			} else {
				setSubStoreList(userData.sub_stores);
			}
		} catch (err) {
			console.log(err);
			showAlert('Ocurrió un problema');
		}

		const getMasterStoreData = async () => {
			try {
				const masterStoreDoc = await firestore.collection('Stores').doc(userData.master_store.id).get();
				const data = masterStoreDoc.data() || {};
				setMasterStorePayment2fa({
					email: userData.master_store.id,
					phone_number: data.phone_number || userData.master_store?.phone_number || '',
					'2fa_phone_number': data['2fa_phone_number'] || userData.master_store?.['2fa_phone_number'] || '',
					'2fa_needed': data['2fa_needed'],
					store_payment_2fa_valid_until: data.store_payment_2fa_valid_until
				});
			} catch (error) {
				console.error('Error loading master store payment 2FA data: ', error);
			}
		};
		getMasterStoreData();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (!prontoPagoAmount) return;
		setAmountPaid((prontoPagoAmount * (country === 'venezuela' && payMethod !== 'divisas' && payMethod !== 'cash' ? exchangeRate : 1)).toFixed(2));
		setAmountReceived((prontoPagoAmount * (country === 'venezuela' && payMethod !== 'divisas' && payMethod !== 'cash' ? exchangeRate : 1)).toFixed(2));
		
		setAmountToPayDollars(prontoPagoAmount);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [prontoPagoAmount, payMethod]);

	const sendPostRequest = async (data, endpoint, token2) => {
		try {
			const response = await fetch(toApiUrl(endpoint), {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					Authorization: `Bearer ${await firebase.auth().currentUser.getIdToken(true)}`,
					'x-master-store-email': userData.master_store.id,
					'x-user-email': userData.email,
					'x-country': country,
					...impersonationHeaders
				},
				body: JSON.stringify({ ...data, country: country })
			});
			const responseData = await response.json();
			if (response.status === '401') {
				const tokenData = await requestToken();
				await setToken('Token ' + tokenData.token);

				const resData = await sendPostRequest(data, endpoint, 'Token ' + tokenData.token);
				return resData;
			}
			return responseData;
		} catch (error) {
			console.error('Error:', error.message);
			return null;
		}
	};

	const requestToken = async () => {
		const loginData = {
			username: process.env.REACT_APP_TOKEN_USERNAME,
			password: process.env.REACT_APP_TOKEN_PASSWORD
		};
		try {
			const response = await fetch(toApiUrl('api/token/'), {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json'
				},
				body: JSON.stringify(loginData)
			});

			const responseData = await response.json();
			return responseData;
		} catch (error) {
			console.error('Error:', error);
		}
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		const generation = searchGeneration.current;
		setIsLoading(true);
		setErrorMessage('');

		try {
			const cedulaData = {
				identification_number: format_identification_with_prefix(cedula, uidPrefix)
			};
			const response = await sendPostRequest(cedulaData, 'registration/usercheck/', token); //status, success, data
			if (generation !== searchGeneration.current) return;
			if (response?.success) {
				if (response.data.is_user_registered) {
					if (response?.data?.user_country !== country) {
						setErrorMessage(response.status);
						setIsLoading(false);
					} else {
						await getPayments();
					}
				} else {
					setErrorMessage(`No encontramos ningun cliente asociado a ${country === 'peru' ? 'este DNI' : 'esta cédula'}.`);
					setIsLoading(false);
				}
			} else if (response?.success === false) {
				setErrorMessage(response.status);
				setIsLoading(false);
			} else {
				setErrorMessage('Estamos teniendo problemas para conectarnos.');
				setIsLoading(false);
			}
		} catch (error) {
			setErrorMessage('Estamos teniendo problemas para conectarnos.');
			setIsLoading(false);
		}
	};

	const fetchDeviceDetails = async (deviceIds, paymentDetails) => {
			const promises = deviceIds.map(async (id) => {
				const deviceDoc = await firestore.collection('Devices').doc(id).get();
				const deviceData = deviceDoc.data();
				paymentDetails[id].name = deviceData.device_commercial_name
					? deviceData.device_commercial_name
					: `${deviceData.device_manufacturer} ${deviceData.device_model}`;
				paymentDetails[id].date = formatDate(deviceData.date_bought);
			});
			await Promise.all(promises);
	};

	const clearPreparedPayment = () => {
		preparationRevision.current += 1;
		if (preparationPending.current) setIsLoading(false);
		preparationPending.current = false;
		preparedSelection.current = null;
		receiverUnsubscribe.current?.();
		receiverUnsubscribe.current = null;
		setPage(2);
		setShowConfirmDialog(false);
		setPendingConfirmAction(null);
		setPaymentDocId('');
		setCombinedPaymentIds([]);
		setProntoPagoNotice({});
		setProntoPagoAmount(null);
		setLoadingProntoPago({});
		setAmountToPayDollars(0);
		setAmountPaid('');
		setAmountReceived('');
		setOnlinePaymentAmount('');
	};

	const getPayments = async () => {
		paymentsUnsubscribe.current?.();
		const generation = ++searchGeneration.current;
		const paymentsCollection = firestore.collection('Payments');

		// Get a reference to the Firestore collection for users
		const usersCollection = firestore.collection('Users');

		// Query the collection based on the "user" field reference
		try {
			const formattedCedula = format_identification_with_prefix(cedula, uidPrefix);
			const queryUser = usersCollection
				.where('identification_number', '==', formattedCedula)
				.limit(1);
			await queryUser
				.get()
				.then((querySnapshot) => {
					if (generation !== searchGeneration.current) return;
					querySnapshot.forEach((doc) => {
						setEmail(doc.id);
						setClientName(doc.data()['first_name'] + ' ' + doc.data()['last_name']);
						const query = paymentsCollection
							.where('user', '==', usersCollection.doc(doc.id))
							.where('is_active', '==', true)
							.orderBy('dueDate', 'asc');
						let initialSnapshot = true;
						paymentsUnsubscribe.current = query.onSnapshot(async (querySnapshot) => {
							if (generation !== searchGeneration.current) return;
							if (preparationPending.current) {
								clearPreparedPayment();
								setErrorMessage('Las cuotas cambiaron. Revisa la selección antes de reportar el pago.');
							}
							const revision = ++snapshotRevision.current;
							const isCurrent = () => generation === searchGeneration.current && revision === snapshotRevision.current;
							const paymentDetails = {};

							const deviceIds = [];
							const showPayments2 = {};
							let isDevicePaymentsShown = true;

							try {
								const promises = querySnapshot.docs.map(async (doc) => {
									const data = doc.data();

									if (paymentDetails.hasOwnProperty(data.device.id)) {
										paymentDetails[data.device.id].payments.push({ ...data, id: doc.id });
									} else {
										paymentDetails[data.device.id] = {
											payments: [{ ...data, id: doc.id }],
											name: data.device.id,
											date: '',
											isDelayed: false
										};
										deviceIds.push(data.device.id);
										if (isDevicePaymentsShown) {
											showPayments2[data.device.id] = true;
											isDevicePaymentsShown = false;
										} else {
											showPayments2[data.device.id] = false;
										}
									}

									const dueDate = data.dueDate.toDate();
									dueDate.setDate(dueDate.getDate() + 1);
									if (!data.paid && dueDate < new Date()) {
										paymentDetails[data.device.id].isDelayed = true;
									}
								});

								await Promise.all(promises);
								await fetchDeviceDetails(deviceIds, paymentDetails);

								if (!isCurrent()) return;
								const prepared = preparedSelection.current;
								if (prepared && prepared.some((previous) => {
									const group = paymentDetails[previous.device.id]?.payments || [];
									const current = group.find((payment) => payment.id === previous.id);
									const ids = new Set(prepared.filter((payment) => payment.device.id === previous.device.id).map((payment) => payment.id));
									const prefixChanged = pendingPayments(group).slice(0, ids.size).some((payment) => !ids.has(payment.id));
									return !current || current.paid || prefixChanged ||
										remainingAmount(current) !== remainingAmount(previous) || paymentReceiver(current) !== paymentReceiver(previous);
								})) {
									clearPreparedPayment();
									setErrorMessage('Las cuotas cambiaron. Revisa la selección antes de reportar el pago.');
								}
								setPayments(paymentDetails);
								setSelectedInstallmentIds((current) => {
									const valid = [];
									Object.values(paymentDetails).forEach((group) => {
										for (const payment of pendingPayments(group.payments)) {
											if (!current.includes(payment.id) || remainingAmount(payment) <= 0) break;
											valid.push(payment.id);
										}
									});
									return valid;
								});
								setShowPayments((previous) => initialSnapshot ? showPayments2 : { ...showPayments2, ...previous });
								if (initialSnapshot && !preparationPending.current) setIsLoading(false);
								initialSnapshot = false;

								if (typeof paymentDetails !== 'object' || !Object.keys(paymentDetails).length) {
									showAlert(
										'No tienes cuotas pendientes. Dirígete a una de nuestras tiendas aliadas para obtener tu equipo a cuotas HOY!'
									);
								}
							} catch (err) {
								if (!isCurrent()) return;
								console.error('Error processing payments:', err.message);
								setErrorMessage('Ocurrió un problema, intente de nuevo más tarde.');
								if (initialSnapshot && !preparationPending.current) setIsLoading(false);
							}
						});
					});
				})
				.catch((error) => {
					setIsLoading(false);
					showAlert('Error: ' + error.message);
				});
		} catch (error) {
			setIsLoading(false);
			showAlert('Error: ' + error.message);
		}
	};

	const showPaymentScreen = async (paymentId, pronto_pago_amount = null, ids = [paymentId], operation = null) => {
		const request = operation || { search: searchGeneration.current, preparation: ++preparationRevision.current };
		const isCurrent = () => request.search === searchGeneration.current && request.preparation === preparationRevision.current;
		if (!isCurrent()) return;
		preparationPending.current = true;
		setIsLoading(true);
		setErrorMessage('');
		preparedSelection.current = null;
		receiverUnsubscribe.current?.();
		try {
			const docs = await Promise.all(ids.map((id) => firestore.collection('Payments').doc(id).get()));
			let chosen = docs.map((doc) => ({ ...doc.data(), id: doc.id }));
			if (docs.some((doc) => !doc.exists) || chosen.some((payment) =>
				payment.paid || payment.is_active === false || payment.user?.id !== email ||
				!Number.isFinite(remainingAmount(payment)) || remainingAmount(payment) <= 0)) {
				throw new Error('Las cuotas cambiaron. Actualiza la selección.');
			}
			const receivers = new Set(chosen.map(paymentReceiver));
			if (receivers.size !== 1 || !paymentReceiver(chosen[0])) {
				throw new Error('Estas compras tienen destinatarios distintos. Repórtalas por separado.');
			}
			const devices = [...new Map(chosen.map((payment) => [payment.device.id, payment.device])).values()];
			const schedules = await Promise.all(devices.map(async (device) => {
				const snapshot = await firestore.collection('Payments').where('device', '==', device).orderBy('dueDate', 'asc').get();
				return snapshot.docs.map((doc) => ({ ...doc.data(), id: doc.id })).filter((payment) => payment.is_active !== false);
			}));
			if (schedules.some((schedule) => {
				const selected = schedule.filter((payment) => ids.includes(payment.id));
				const prefix = pendingPayments(schedule).slice(0, selected.length);
				return selected.some((payment) => !prefix.some((earlier) => earlier.id === payment.id));
			})) throw new Error('Cambió la próxima cuota de esta compra. Actualiza la selección.');
			chosen = schedules.flat().filter((payment) => ids.includes(payment.id));
			if (chosen.length !== ids.length || chosen.some((payment) => payment.paid ||
				payment.user?.id !== email || !Number.isFinite(remainingAmount(payment)) || remainingAmount(payment) <= 0) ||
				new Set(chosen.map(paymentReceiver)).size !== 1) {
				throw new Error('Las cuotas cambiaron. Actualiza la selección.');
			}
			const amount = selectionTotal(chosen);
			const baseAmount = pronto_pago_amount ?? amount;
			const receiverId = paymentReceiver(chosen[0]);
			const countryReceiver = country === 'venezuela' && receiverId === 'default';
			const [methods, rate] = await Promise.all([
				countryReceiver ? null : firestore.collection('StorePaymentMethods').doc(receiverId).get(),
				country === 'venezuela' ? firestore.collection('DollarPrice').doc('dollarPrice').get() : null
			]);
			if (!isCurrent()) return;
			const bcv = country === 'venezuela' ? Number(rate?.data()?.bcv) : 1;
			if (!Number.isFinite(bcv) || bcv <= 0) throw new Error('No se pudo obtener la tasa BCV. Intenta nuevamente.');
			const applyMethods = (data) => {
				setPagoMovilData(data?.pagomovil || {});
				setTransferData(data?.transferBs || {});
				setTransferDataUsd(data?.transferUsd || {});
			};
			applyMethods(methods?.data());
			setPaymentReceiver(receiverId);
			if (country === 'venezuela' && !countryReceiver) {
				let previousMethods = paymentMethodsSignature(methods.data());
				receiverUnsubscribe.current = firestore.collection('StorePaymentMethods').doc(paymentReceiver(chosen[0]))
					.onSnapshot((snapshot) => {
						if (!isCurrent()) return;
						const updated = snapshot.data() || {};
						const updatedMethods = paymentMethodsSignature(updated);
						if (updatedMethods !== previousMethods) {
							previousMethods = updatedMethods;
							applyMethods(updated);
							setPayMethod('');
							setErrorMessage(RECEIVER_CHANGED_MESSAGE);
						}
					}, () => {
						if (!isCurrent()) return;
						applyMethods({});
						setPayMethod('');
						setErrorMessage('No se pudieron actualizar los datos del destinatario. Vuelve a seleccionar las cuotas.');
					});
			}
			preparedSelection.current = pronto_pago_amount ? schedules.flatMap(pendingPayments) : chosen;
			setProntoPagoAmount(pronto_pago_amount);
			setProntoPagoNotice(pronto_pago_amount ? {
				message: `Se ha aplicado un descuento por pronto pago. El monto a pagar es: ${CountrySettings.currency_code}${pronto_pago_amount.toFixed(2)}`,
				type: 'success'
			} : {});
			setCombinedPaymentIds(pronto_pago_amount ? [] : ids);
			setPaymentDocId(paymentId);
			setAmountToPayDollars(baseAmount);
			setOnlinePaymentAmount(baseAmount.toFixed(2));
			setAmountPaid('');
			setAmountReceived('');
			setPaymentProof(null);
			setPayMethod(country === 'bolivia' ? 'online_bo' : '');
			setExchangeRate(bcv);
		} catch (error) {
			if (isCurrent()) showAlert(error.message || 'No se pudo preparar el pago.');
		} finally {
			if (isCurrent()) {
				preparationPending.current = false;
				setIsLoading(false);
				setLoadingProntoPago({});
			}
		}
	};

	function convertToDotDecimal(str) {
		if (/^[+-]?(\d{1,3}(,\d{3})*|\d+)(,\d+)?$/.test(str)) {
			return str.replace(/,/g, '.');
		} else {
			return str;
		}
	}

	const isNumber = async (value) => {
		return /^[+-]?(\d{1,3}(,\d{3})*|\d+)(\.\d+)?(,\d+)?$/.test(value);
	};

	const storePayment2faEnabled = masterStorePayment2fa?.['2fa_needed'] === true;
	const storePayment2faAlwaysRequiredThreshold = Number(CountrySettings?.store_payment_2fa_always_required_threshold ?? Infinity);
	const amountReceivedNumber = parseFloat(convertToDotDecimal(amountReceived || '0'));
	const storePayment2faAlwaysRequired =
		storePayment2faEnabled &&
		Number.isFinite(storePayment2faAlwaysRequiredThreshold) &&
		amountReceivedNumber >= storePayment2faAlwaysRequiredThreshold;

	const isStorePayment2faRecentlyValid = () => {
		const validUntil = masterStorePayment2fa?.store_payment_2fa_valid_until;
		const validUntilMs =
			typeof validUntil?.toMillis === 'function' ? validUntil.toMillis() : new Date(validUntil || 0).getTime();
		return Number.isFinite(validUntilMs) && validUntilMs > Date.now();
	};

	const shouldCollectStorePayment2fa =
		payMethod === 'cash' && storePayment2faEnabled && (storePayment2faAlwaysRequired || !isStorePayment2faRecentlyValid());

	const markStorePayment2faValid = () => {
		setMasterStorePayment2fa((current) => ({
			...current,
			store_payment_2fa_valid_until: new Date(Date.now() + 4 * 60 * 60 * 1000).toISOString()
		}));
	};

	const storePayment2faPhoneNumbers = Array.from(
		new Set([masterStorePayment2fa?.['2fa_phone_number'], masterStorePayment2fa?.phone_number].filter(Boolean))
	);
	const storePayment2faMethods = [
		{
			type: 'email',
			email: masterStorePayment2fa?.email || userData?.master_store?.id,
			allowEmailInput: false,
			label: 'Email'
		},
		...storePayment2faPhoneNumbers.flatMap((phoneNumber) => [
			{
				type: 'whatsapp',
				phoneNumber,
				phoneCountry: country,
				label: `WhatsApp · ${phoneNumber}`
			},
			{
				type: 'sms',
				phoneNumber,
				phoneCountry: country,
				label: `SMS · ${phoneNumber}`
			}
		])
	].filter((method) => method.email || method.phoneNumber);

	const setStorePayment2faCode = (code) => {
		if (!storePayment2faSelectedMethod) return;
		setStorePayment2faCodeInput(code);
		setStorePayment2fa(
			code.length === 6
					? {
							code,
							method: storePayment2faSelectedMethod.type,
							destination: storePayment2faSelectedMethod.destination
						}
					: null
		);
	};

	const openStore2faPhoneEditor = () => {
		setStore2faPhoneValue(masterStorePayment2fa?.['2fa_phone_number'] || '');
		setStore2faPhoneCodeSent(false);
		setStore2faPhoneCode('');
		setStore2faPhoneMessage('');
		setStore2faPhoneError('');
		setShowStore2faPhoneEditor(true);
	};

	const saveStore2faPhoneNumber = async () => {
		setStore2faPhoneError('');
		setStore2faPhoneMessage('');

		if (!validate_phone_number(store2faPhoneValue, country)) {
			setStore2faPhoneError('Ingrese un número de teléfono válido.');
			return;
		}
		if (store2faPhoneCode.length !== 6) {
			setStore2faPhoneError('Ingrese el código enviado al email master.');
			return;
		}

		setStore2faPhoneLoading(true);
		const [response, error] = await callAPI(
			'https://api.krece.app/storeprofile/perfil/2fa-phone/',
			'PATCH',
			{ phone_number: store2faPhoneValue, confirmation_code: parseInt(store2faPhoneCode) },
			userData,
			country
		);
		setStore2faPhoneLoading(false);

		if (error) {
			setStore2faPhoneError(error);
		} else if (response?.success) {
			setMasterStorePayment2fa((current) => ({
				...current,
				'2fa_phone_number': response.phone_number || store2faPhoneValue
			}));
			setShowStore2faPhoneEditor(false);
			setStore2faPhoneMessage(response.status || 'Número 2FA actualizado.');
		}
	};

	const confirmPayment = async () => {
		await updatePaymentDoc(amountReceived);
	};

	const updatePaymentDoc = async () => {
		try {
			const paymentAmount = parseFloat(convertToDotDecimal(amountReceived));
			const response = await sendStorePaymentConfirmation();
			if (response?.success) {
				if (payMethod === 'cash' && storePayment2faEnabled) markStorePayment2faValid();
				setStorePaymentReceipt({
					amount: paymentAmount,
					currencyCode: CountrySettings.currency_code || '',
					clientName,
					identificationLabel: country === 'peru' ? 'DNI' : 'Cédula',
					identificationNumber: cedula,
					storeName: userData.master_store?.store_name || userData.store_name || 'Tienda aliada Krece',
					branchName: subStore,
					confirmationCode: String(response.payment_confirmation_code ?? paymentDocId),
					paidAt: new Date().toISOString()
				});
				setSuccess(true);
				setAmountReceived('');
				setIsLoading(false);
			} else if (response?.success === false) {
				setIsLoading(false);
				setErrorMessage(response.status);
			} else {
				setIsLoading(false);
				setErrorMessage('Ocurrió un problema');
			}
		} catch (err) {
			showAlert('Ocurrio un error');
			setIsLoading(false);
		}
	};

	const sendStorePaymentConfirmation = async () => {
		const data = {
			...paymentTargetPayload,
			payment_amount: parseFloat(convertToDotDecimal(amountReceived)),
			store_name: userData.master_store.id,
			sub_store: subStore,
			identification_number: format_identification_with_prefix(cedula, uidPrefix),
			store_user: userData.email,
			...(storePayment2fa?.code && {
				store_payment_2fa_code: storePayment2fa.code,
				store_payment_2fa_method: storePayment2fa.method,
				store_payment_2fa_destination: storePayment2fa.destination
			}),
			...(prontoPagoNotice?.type === 'success' && { early_payment: true })
		};
		return await sendPostRequest(data, 'payments/processstorepayment/', token); //status, success, data
	};

	const handleUnverifiedPaymentSubmit = async () => {
		setIsLoading(true);
		try {
			if (payMethod === 'transfer_rd') {
				if (!paymentDate || !accountNumber || !accountHolder || !paymentProof) {
					setErrorMessage('Llene todos los datos para continuar.');
					setIsLoading(false);
					return;
				}
			} else if (payMethod === 'deposito_rd') {
				if (!paymentDate || !paymentProof) {
					setErrorMessage('Llene todos los datos para continuar.');
					setIsLoading(false);
					return;
				}
			} else {
				setErrorMessage('Método inválido.');
				setIsLoading(false);
				return;
			}

			const formData = {
				user_document_id: email,
				...paymentTargetPayload,
				amount: parseFloat(amountToPayDollars),
				date: paymentDate,
				country: country,
				payment_confirmation_url: paymentProof,
				reference: '',
				account_number: payMethod === 'transfer_rd' ? (accountNumber?.trim() || '') : '',
				account_owner: payMethod === 'transfer_rd' ? (accountHolder?.trim() || '') : '',
				...(prontoPagoNotice?.type === 'success' ? { early_payment: true } : {})
			};

			const response = await sendPostRequest(formData, 'payments/processunverifiedpayment/', token);

			if (response?.success) {
				setSuccess(true);
				setIsLoading(false);
			} else {
				setErrorMessage(response?.status || 'Ocurrió un problema al procesar el pago.');
				setIsLoading(false);
			}
		} catch (err) {
			setErrorMessage('Ocurrió un error: ' + err.message);
			setIsLoading(false);
		}
	};

	const handlePaymentSubmit = async (e) => {
		e.preventDefault();
		if (country === 'bolivia') {
			const parsedAmount = parseFloat(convertToDotDecimal(onlinePaymentAmount || '0'));
			if (!onlinePaymentAmount || Number.isNaN(parsedAmount) || parsedAmount <= 0) {
				setErrorMessage('Ingrese un monto válido.');
				return;
			}
			setIsLoading(true);
			await handleOnlinePaymentBolivia(parsedAmount);
			return;
		}
		if (payMethod === 'cash') {
			if (!amountReceived || !subStore) {
				setErrorMessage('Llene todos los datos para continuar.');
				return;
			} else if ((await isNumber(amountReceived)) === false) {
				setErrorMessage('El monto a pagar debe ser expresado en números.');
				return;
			} else if (prontoPagoNotice?.type !== 'success' && amountReceived > CountrySettings.max_cuota_payment) {
				setErrorMessage(
					'El monto máximo por registro es de ' +
						CountrySettings.currency_code +
						CountrySettings.max_cuota_payment +
						', si desea registrar más del máximo puede realizarlo en dos (o más) registros.'
				);
				return;
			} else if (shouldCollectStorePayment2fa && !storePayment2fa?.code) {
				setErrorMessage('Por favor complete la verificación de 2FA para continuar');
				return;
			}

			showConfirm(`¿Deseas continuar con este registro de ${CountrySettings.currency_code}${amountReceived}?`, async () => {
				setIsLoading(true);
				try {
					await confirmPayment();
				} catch (error) {
					setErrorMessage('Estamos teniendo problemas para conectarnos.');
					setIsLoading(false);
				}
			});
			return;
		}

		if (payMethod === 'transfer_rd' || payMethod === 'deposito_rd') {
			handleUnverifiedPaymentSubmit();
			return;
		}

		if (usesCountryPaymentDetails && (
			(payMethod === 'pagomovil' && !pagoMovilAccounts.length) ||
			(payMethod === 'transfer' && !transferAccounts.length) ||
			(payMethod === 'divisas' && !transferUsdAccounts.length)
		)) {
			showAlert('Este método de pago ya no está disponible. Seleccione otro método.');
			return;
		}
		if (usesCountryPaymentDetails && ['pagomovil', 'transfer'].includes(payMethod) && !receiverAvailable) {
			showAlert('El banco receptor ya no está activo para este método. Seleccione otro banco.');
			return;
		}
		if (!confirmationNumber || !amountPaid || !paymentDate || !payMethod) {
			showAlert('Llene todos los datos para continuar.');
			return;
		} else if (payMethod === 'pagomovil' && !bank) {
			showAlert('Seleccione el banco de origen.');
			return;
		} else if (usesCountryPaymentDetails && (selectedReceiver === 'mercantil' || (selectedReceiver === 'r4' && phoneNumber)) && payMethod === 'pagomovil' && !validate_phone_number(phoneNumber, 'venezuela')) {
			showAlert('Ingrese el teléfono completo de origen.');
			return;
		} else if (usesCountryPaymentDetails && selectedReceiver === 'mercantil' && payMethod === 'transfer' && !bank) {
			showAlert('Seleccione el banco emisor.');
			return;
		} else {
			setIsLoading(true);
			try {
				const response = await sendPaymentConfirmation();
				if (response?.success) {
					setIsLoading(false);
					setSuccess(true);
					showAlert('Su pago fue confirmado exitosamente');
				} else if (response?.success === false) {
					setIsLoading(false);
					showAlert('Error: ' + response?.status);
				} else {
					showAlert('Ocurrió un problema intente más tarde');
					setIsLoading(false);
				}
			} catch (err) {
				showAlert(err.message);
				setIsLoading(false);
			}
		}
	};

const handleOnlinePaymentBolivia = async (amountValue) => {
	setErrorMessage('');
	try {
		const data = {
			amount: amountValue,
			identification_number: format_identification_with_prefix(cedula, uidPrefix),
			...paymentTargetPayload,
			user_document_id: email,
			...(prontoPagoNotice?.type === 'success' && { early_payment: true })
		};
		const response = await sendPostRequest(data, 'payments/libelulaurl/', token);
		if (response?.success) {
			const targetUrl = response?.checkout_url || response?.data?.checkout_url;
			if (targetUrl) {
				const newTab = window.open(targetUrl, '_blank', 'noopener,noreferrer');
				if (newTab) newTab.focus();
				else window.location.href = targetUrl;
			} else {
				setErrorMessage('No se recibió la URL de pago.');
			}
		} else if (response?.success === false) {
			setErrorMessage(response?.status || 'Ocurrió un problema al procesar el pago en línea.');
		} else {
			setErrorMessage('Ocurrió un problema al procesar el pago en línea.');
		}
	} catch (err) {
		setErrorMessage('Ocurrió un problema al procesar el pago en línea.');
	} finally {
		setIsLoading(false);
	}
};

	function formatForAPI(date) {
		// Format the date as yyyy/mm/dd
		const formattedDate = date
			.toLocaleString('en-CA', {
				year: 'numeric',
				month: '2-digit',
				day: '2-digit',
				timeZone: 'America/New_York' // GMT-4 timezone (Eastern Time)
			})
			.replace(/\//g, '-'); // Replace slashes with dashes

		return formattedDate;
	}

	function replaceFirstCharWith58(originalString) {
		if (typeof originalString !== 'string' || originalString.length === 0) {
			// Handle invalid input or empty string
			return originalString;
		}

		const modifiedString = '58' + originalString.substring(1);
		return modifiedString;
	}

	const sendPaymentConfirmation = async () => {
		//todo: check if amountPaid is numerical before sending the call
		let data;
		if (payMethod === 'divisas') {
			data = {
				...paymentTargetPayload,
				reference: confirmationNumber,
				amount: convertToDotDecimal(amountPaid),
				payment_type: 'TRANSFER',
				date: formatForAPI(paymentDate),
				api_type: 'bnc_cash',
				country: 'venezuela',
				...(prontoPagoNotice?.type === 'success' && { early_payment: true })
			};
		} else if (payMethod === 'transfer') {
			data = {
				...paymentTargetPayload,
				reference: confirmationNumber,
				amount: convertToDotDecimal(amountPaid),
				payment_type: 'TRANSFER',
				date: formatForAPI(paymentDate),
				api_type: usesCountryPaymentDetails ? selectedReceiver : 'banesco',
				...(bank && { bank_code: bank.split(' - ')[0] }),
				...(issuerCustomerId && { issuer_customer_id: issuerCustomerId }),
				...(prontoPagoNotice?.type === 'success' && { early_payment: true })
			};
		} else {
			data = {
				...paymentTargetPayload,
				reference: confirmationNumber,
				amount: convertToDotDecimal(amountPaid),
				payment_type: 'P2C',
				phone_num: replaceFirstCharWith58(phoneNumber),
				...(bank && { bank_code: bank.split(' - ')[0] }),
				date: formatForAPI(paymentDate),
				api_type: usesCountryPaymentDetails ? selectedReceiver : 'banesco',
				...(prontoPagoNotice?.type === 'success' && { early_payment: true })
			};
		}
		try {
			const tokenData = await requestToken();
			const manualBankSelection =
				internal && usesCountryPaymentDetails && ['pagomovil', 'transfer'].includes(payMethod);
			const firebaseToken = manualBankSelection ? await Firebase.auth().currentUser?.getIdToken() : null;
			const response = await fetch(toApiUrl('process_payment/'), {
				//agregar un catch aqui
				method: 'POST',
				// mode: 'cors',
				headers: {
					'Content-Type': 'application/json',
					Authorization: firebaseToken ? 'Bearer ' + firebaseToken : 'Token ' + tokenData.token,
					...(firebaseToken && { 'X-User-Email': userData.email }),
					...impersonationHeaders
				},
				body: JSON.stringify(data)
			});
			const responseData = await response.json();
			return responseData;
		} catch (error) {
			console.error('Error:', error.message);
			return null;
		}
	};

	const formatDate = (timestamp) => {
		const date = timestamp.toDate(); // Convert Firestore timestamp to JavaScript Date object
		const month = date.toLocaleDateString('es-ES', { month: 'short' }); // Get the short month name
		const day = date.getDate(); // Get the day of the
		return `${day} de ${month.charAt(0).toUpperCase() + month.slice(1)}`;
	};

	// Function to handle file upload and convert to base64
	const handleFileUpload = async (e) => {
		if (uploadingProof) return;
		setUploadingProof(true);
		setPaymentProof(null);

		const DOC_TYPES = ['image/jpeg', 'image/png', 'image/bmp', 'image/tiff', 'image/webp'];

		try {
			const file = e.target.files?.[0];
			if (!file) throw new Error();

			const is_valid_file = file.size && DOC_TYPES.includes(file.type);
			const extension = file.name.split('.')?.pop()?.toLowerCase();
			const size = file.size;

			if (!is_valid_file || !extension) {
				setErrorMessage('Debe subir una imagen válida.');
				throw new Error();
			} else if (size > 5000000) {
				setErrorMessage('El tamaño de la imagen seleccionada no puede superar los 5MB.');
				throw new Error();
			}

			const storage = Firebase.storage();
			const storageRef = storage.ref();
			const fileName = `${Date.now()}_payment_proof.${extension}`;
			const fileRef = storageRef.child(fileName);
			await fileRef.put(file, {
				cacheControl: 'public,max-age=31536000,immutable'
			});

			const downloadURL = await fileRef.getDownloadURL();
			if (downloadURL) {
				setPaymentProof(downloadURL);
			} else {
				throw new Error();
			}
		} catch (error) {
			setErrorMessage('Error al subir la imagen.');
		} finally {
			setUploadingProof(false);
		}
	};

	const GetPaymentObjects = (paymentList) => {
		var isFuturePayment = true;
		const paym = [];

		paymentList.forEach((item) => {
			var PayObj;
			if (item.paid && item.checked_by_internal_team) {
				 PayObj = (
					<div className="flex justify-center">
						<span className="text-sm text-primary-500">PAGADO</span>
					</div>
				);
			} else if (item.paid && !item.checked_by_internal_team) {
				PayObj = (
					<div className="flex justify-center">
						<span className="text-sm text-neutral-400">Por Confirmar</span>
					</div>
				);
			} else {
				PayObj = <div className="flex justify-center"></div>;
			}

			if (isFuturePayment && !item.paid) {
				PayObj = (
					<Button
						disabled={isLoading || isPaymentBlocked || country === 'colombia'}
						loading={isLoading && item.id === selectedPaymentId}
						onClick={() => {
							setSelectedPaymentId(item.id);
							showPaymentScreen(item.id);
						}}
						variant="primary"
						className={`!h-8 !w-min !justify-self-center`}
					>
						PAGAR
					</Button>
				);
				isFuturePayment = false;
			}

			if (selectionMode && !item.paid) {
				PayObj = (
					<div className="flex justify-center">
						<Checkbox
							aria-label={`Seleccionar cuota del ${formatDate(item.dueDate)}`}
							checked={selectedInstallmentIds.includes(item.id)}
							disabled={isLoading || isPaymentBlocked || country === 'colombia'}
							onCheckedChange={() => toggleInstallment(item, paymentList)}
						/>
					</div>
				);
			}

			// A pending receipt is informational; only an applied payment removes the balance.
			if (hasPendingReview(item) && !item.paid) {
				PayObj = (
					<div className="flex flex-col items-center gap-2">
						<span className="text-sm text-neutral-400">Por Confirmar</span>
						{PayObj}
					</div>
				);
			}

			paym.push(
				<div key={item.id} className="grid grid-cols-3 items-center px-2 py-3">
					<span className="text-left text-sm text-neutral-300">{formatDate(item.dueDate)}</span>
					{PayObj}
					<span className="text-right text-sm text-neutral-300">
						{CountrySettings.currency_code + (item.actualAmount - item.amountPaid).toFixed(2)}
					</span>
				</div>
			);
		});
		return paym;
	};

	const steps = [
		{ label: 'Buscar Cliente', page: 1 },
		{ label: 'Seleccionar Cuotas', page: 2 },
		{ label: 'Realizar Pago', page: 3 },
		{ label: 'Pago Exitoso', page: 4 }
	];

	// Check if the device is eligible for Pronto Pago
	const isEligibleForProntoPago = (deviceId) => {
		if (country === 'colombia' || selectionMode || isLoading) return false;
		if (!deviceId || !payments || Object.keys(payments).length === 0) return false;

		// Find the device in payments
		const devicePayments = payments[deviceId];
		if (!devicePayments) return false;

		// Check if it has at least 2 unpaid payments
		const unpaidPayments = devicePayments.payments.filter((payment) => !payment.paid);
		if (unpaidPayments.length < 2) return false;

		// Check if any payment has unverified_payment
		const hasUnverifiedPayments = devicePayments.payments.some(hasPendingReview);

		return !hasUnverifiedPayments;
	};

	// Handle Pronto Pago button click
	const handleProntoPago = async (deviceId) => {
		const operation = { search: searchGeneration.current, preparation: ++preparationRevision.current };
		const isCurrent = () => operation.search === searchGeneration.current && operation.preparation === preparationRevision.current;
		preparationPending.current = true;
		setIsLoading(true);
		// Set loading state for this device
		setLoadingProntoPago((prev) => ({ ...prev, [deviceId]: true }));
		setProntoPagoNotice({});
		setProntoPagoAmount(null);
		try {
			// Find the first unpaid payment for the device
			const devicePayments = payments[deviceId];
			const firstUnpaidPayment = devicePayments.payments.find((payment) => !payment.paid);

			if (!firstUnpaidPayment) {
				setLoadingProntoPago((prev) => ({ ...prev, [deviceId]: false }));
				return;
			}

			// Apply pronto pago discount
			const data = {
				country: country,
				device_object_id: deviceId
			};
			const response = await sendPostRequest(data, 'payments/getdiscountedpayments/', token);
			if (!isCurrent()) return;

			if (response?.success && response.data && response.data.new_total_pending_amount) {
				// Navigate to payment screen (set up the same way as showPaymentScreen)
				setSelectedPaymentId(firstUnpaidPayment.id);

				// This will trigger the showPaymentScreen logic
				await showPaymentScreen(firstUnpaidPayment.id, response.data.new_total_pending_amount, [firstUnpaidPayment.id], operation);
			} else {
				setProntoPagoNotice({
					message: 'No se pudo obtener el descuento por pronto pago para este dispositivo.',
					type: 'error'
				});
				setProntoPagoAmount(null);
				setLoadingProntoPago((prev) => ({ ...prev, [deviceId]: false }));
			}
		} catch (error) {
			if (!isCurrent()) return;
			setProntoPagoNotice({
				message: 'Ocurrió un error al procesar la solicitud de pronto pago.',
				type: 'error'
			});
			setProntoPagoAmount(null);
			setLoadingProntoPago((prev) => ({ ...prev, [deviceId]: false }));
		} finally {
			if (isCurrent()) {
				preparationPending.current = false;
				setIsLoading(false);
				setLoadingProntoPago({});
			}
		}
	};

	if (!authenticated || !userData || !CountrySettings) return <ContainerSpinner size={3} />;

	return (
		<div className="m-auto flex min-h-full min-w-full flex-col items-center justify-start">
			<Stepper currentPage={page} steps={steps} />

			<div className="m-auto flex h-full w-full flex-col items-center justify-center p-4 md:p-6">
				<div
					className={classNames(
						'w-[500px] max-w-full rounded-xl border-[1.5px] border-neutral-900 bg-neutral-1000/25 p-6 shadow-lg duration-300 animate-in fade-in slide-in-from-bottom-5 light:bg-white light:shadow-none md:bg-background/50 md:p-8',
						page === 2 && 'w-[650px]'
					)}
				>
					{isInactive && country === 'bolivia' && (
						<Notice
							message="Tu tienda está inhabilitada para nuevas ventas. Puedes generar QRs para que tus clientes paguen sus cuotas."
							variant="info"
							className="mb-4"
						/>
					)}
					{page === 1 && (
						<>
							{render_page_header(
								'Registrar Pago',
								`Por favor ingrese ${country === 'peru' ? 'el DNI' : 'la cédula'} del cliente que deseas buscar`,
								<Search />
							)}

							<form className="space-y-4" onSubmit={handleSubmit}>
								<Input
									name="cedula"
									floatingLabel={`${country === 'peru' ? 'DNI' : 'Cédula'} del cliente`}
									value={cedula}
									onChange={(value) => setCedula(value)}
									placeholder={`Ingrese ${country === 'peru' ? 'el DNI' : 'la cédula'} del cliente`}
									allowedChars={'numbers_only'}
								/>

								{errorMessage && <Notice message={errorMessage} variant="error" />}

								<Button
									type="submit"
									onClick={handleSubmit}
									loading={isLoading}
									disabled={isLoading || !cedula}
									variant="primary"
								>
									Continuar
								</Button>
							</form>
						</>
					)}

					{page === 2 && (
						<>
							{render_page_header('Mis Cuotas', null, <List />)}

							<Button
								variant="secondary"
								className="mb-4"
								disabled={isLoading || country === 'colombia' || isPaymentBlocked}
								onClick={() => {
									clearPreparedPayment();
									setSelectionMode(!selectionMode);
									setSelectedInstallmentIds([]);
								}}
							>
								{selectionMode ? 'Cancelar selección' : 'Seleccionar varias cuotas'}
							</Button>
							{errorMessage && <Notice className="mb-4" message={errorMessage} variant="warning" />}
							{selectionMode && <Notice className="mb-4" variant="info"
								message="Puedes combinar cuotas de varias compras. Se incluyen primero las cuotas pendientes más antiguas de cada compra." />}

							{Object.keys(payments).length > 0 && (
								<div className="space-y-4">
									<div className="flex items-center justify-center gap-3 rounded-xl border border-neutral-700/50 bg-neutral-800/50 p-3 text-center light:bg-background sm:gap-6 md:p-4">
										<div className="flex w-[65%] flex-col items-center">
											<span className="self-start text-sm font-medium text-neutral-400">Cliente</span>
											<span
												className="w-full truncate text-left font-semibold text-primary-400 light:text-primary-600 md:text-lg"
												title={clientName}
											>
												{clientName}
											</span>
										</div>

										<div className="h-8 w-[1px] bg-neutral-700/50"></div>

										<div className="flex w-[35%] flex-col items-center">
											<span className="self-end text-sm font-medium text-neutral-400">
												{country === 'peru' ? 'DNI' : 'Cédula'}
											</span>
											<span
												className="w-full truncate text-right font-semibold text-primary-400 light:text-primary-600 md:text-lg"
												title={cedula}
											>
												{cedula}
											</span>
										</div>
									</div>

									{Object.keys(payments).map((item) => (
										<div
											key={item}
											className="rounded-xl border border-neutral-700/50 bg-neutral-800/50 p-4 transition-colors hover:border-neutral-600/50 light:bg-background"
										>
											<button
												className="group relative flex w-full items-center justify-between"
												onClick={() => setShowPayments({ ...showPayments, [item]: !showPayments[item] })}
											>
												<div className="flex flex-col items-start">
													<span className="text-lg font-medium text-neutral-0 transition-colors group-hover:text-primary-400">
														{payments[item].name}
													</span>
													<span className="text-sm text-neutral-400">{'Comprado el ' + payments[item].date}</span>
												</div>

												{isEligibleForProntoPago(item) && !isPaymentBlocked && (
													<button
														onClick={(e) => {
															e.stopPropagation(); // Prevent accordion toggle
															handleProntoPago(item);
														}}
														disabled={loadingProntoPago[item]}
														className="absolute -right-3.5 -top-3.5 flex scale-90 items-center gap-1 rounded-full bg-primary-500 px-2 py-0.5 text-xs font-medium text-black transition-colors hover:bg-primary-600 disabled:opacity-50"
														title="Pronto Pago"
													>
														{loadingProntoPago[item] ? <Spinner size={0.5} color="black" /> : <BadgePercent className="size-3" />}
														<span>Pronto Pago</span>
													</button>
												)}

												<div className="flex items-center gap-3">
													{payments[item].isDelayed && (
														<div className="flex scale-90 animate-pulse flex-col items-center">
															<AlertTriangle className="size-5 text-red-500" />
															<span className="text-xs font-medium text-red-500">Cuota vencida</span>
														</div>
													)}

													{showPayments[item] ? (
														<ChevronUp className="size-5 text-neutral-400 transition-colors group-hover:text-primary-400" />
													) : (
														<ChevronDown className="size-5 text-neutral-400 transition-colors group-hover:text-primary-400" />
													)}
												</div>
											</button>

											{showPayments[item] && (
												<div className="mt-4 space-y-4">
													<div className="grid grid-cols-3 border-b border-neutral-700/50 px-2 pb-2 text-sm font-medium text-neutral-400">
														<span className="text-left">Vence</span>
														<span className="text-center">Estado</span>
														<span className="text-right">Debe</span>
													</div>

													<div className="divide-y divide-neutral-700/30">{GetPaymentObjects(payments[item].payments)}</div>

													<div className="flex justify-between border-t border-neutral-700 pt-4">
														<span className="font-medium text-neutral-200">Saldo restante</span>
														<span className="font-medium text-primary-400">
															{CountrySettings.currency_code +
																formatNumber(
																	payments[item].payments
																		.reduce((total, payment) => total + (payment.actualAmount - payment.amountPaid), 0)
																		.toFixed(2)
																)}
														</span>
													</div>

													{isEligibleForProntoPago(item) && !isPaymentBlocked && (
														<div className="mt-4 border-t border-neutral-700 pt-4">
															<Button
																variant="primary"
																className="w-full"
																loading={loadingProntoPago[item]}
																disabled={loadingProntoPago[item]}
																onClick={() => handleProntoPago(item)}
															>
																Pronto Pago
															</Button>
														</div>
													)}
												</div>
											)}
										</div>
									))}
								</div>
							)}

							{selectionMode && (
								<div className="mt-4 space-y-3 rounded-xl border border-neutral-700/50 p-4">
									{Object.entries(payments).map(([id, group]) => {
										const selected = group.payments.filter((payment) => selectedInstallmentIds.includes(payment.id));
										return selected.length > 0 && (
											<div key={id} className="flex justify-between gap-3 text-sm text-neutral-300">
												<span>{group.name} · {selected.length} cuota{selected.length === 1 ? '' : 's'}</span>
												<span>{CountrySettings.currency_code}{formatNumber(selected.reduce((sum, payment) => sum + remainingAmount(payment), 0))}</span>
											</div>
										);
									})}
									<div className="flex justify-between gap-3 font-medium text-neutral-100">
										<span>Total de {selectedInstallments.length} cuotas</span>
										<span>{CountrySettings.currency_code}{formatNumber(selectedTotal)}</span>
									</div>
									<Button loading={isLoading} disabled={isLoading || !selectedInstallments.length}
										onClick={() => showPaymentScreen(selectedInstallments[0].id, null, selectedInstallments.map((payment) => payment.id))}>
										Continuar
									</Button>
								</div>
							)}

							<Button disabled={isLoading} onClick={resetAll} Icon={<ArrowLeft className="size-5" />} variant="secondary" className="mt-4 w-full">
								Atrás
							</Button>
						</>
					)}

					{page === 3 && (
						<>
							{render_page_header(combinedPaymentIds.length > 1 ? `Pago de ${combinedPaymentIds.length} cuotas` : 'Pago de Cuota', 'Ingresa los detalles del pago', <Wallet />)}

							<div className="space-y-6">
								<div className="-mb-2 flex items-center justify-center gap-3 rounded-xl border border-neutral-700/50 bg-neutral-800/50 p-3 text-center light:bg-background sm:gap-6 md:p-4">
									<div className="flex w-[65%] flex-col items-center">
										<span className="self-start text-sm font-medium text-neutral-400">Cliente</span>
										<span
											className="w-full truncate text-left font-semibold text-primary-400 light:text-primary-600 md:text-lg"
											title={clientName}
										>
											{clientName}
										</span>
									</div>

									<div className="h-8 w-[1px] bg-neutral-700/50"></div>

									<div className="flex w-[35%] flex-col items-center">
										<span className="self-end text-sm font-medium text-neutral-400">{country === 'peru' ? 'DNI' : 'Cédula'}</span>
										<span
											className="w-full truncate text-right font-semibold text-primary-400 light:text-primary-600 md:text-lg"
											title={cedula}
										>
											{cedula}
										</span>
									</div>
								</div>
								<div
									className={`rounded-xl border border-neutral-700/50 bg-neutral-800/50 p-4 light:bg-neutral-1000 ${country === 'venezuela' ? '!-mb-2' : ''}`}
								>
									<span className="text-sm text-neutral-400">Monto a pagar</span>
									<div className="mt-1 text-lg font-medium text-primary-400">
										{CountrySettings.currency_code +
											(country === 'bolivia'
												? (parseFloat(onlinePaymentAmount || amountToPayDollars || 0) || 0).toFixed(2)
												: prontoPagoAmount?.toFixed(2) || amountToPayDollars?.toFixed(2)) +
											(country === 'venezuela'
												? ' / Bs. ' + ((prontoPagoAmount || amountToPayDollars) * exchangeRate).toFixed(2) + ' '
												: '')}
										{country === 'venezuela' && <span className="text-sm text-neutral-400"> (A tasa BCV)</span>}
									</div>
								</div>

								{country === 'venezuela' && (
									<div className="rounded-xl border border-neutral-700/50 bg-neutral-800/50 p-4 light:bg-neutral-1000">
										<h2 className="mb-4 text-lg font-medium text-neutral-100">Detalles de las cuentas a pagar</h2>
										{!pagoMovilAccounts.length && !transferAccounts.length && !(internal && transferUsdAccounts.length) && (
											<Notice message="No hay cuentas de pago disponibles en este momento." variant="warning" />
										)}

										<div className="space-y-4">
											{pagoMovilAccounts.map((account, index) => (
												<div key={index}>
													<h3 className="mb-2 text-sm font-medium text-primary-400">PagoMovil</h3>
													<div className="space-y-1 text-sm text-neutral-300">
														<p>Banco: {account.bank}</p>
														<p>Rif: {account.rif}</p>
														<p>Teléfono: {account.phoneNumber}</p>
													</div>
												</div>
											))}
											{transferAccounts.map((account, index) => (
												<div key={index}>
													<h3 className="mb-2 text-sm font-medium text-primary-400">Transferencia Bolívares</h3>
													<div className="space-y-1 text-sm text-neutral-300">
														<p>Cuenta: {account.account}</p>
														<p>Rif: {account.rif}</p>
														<p>Banco: {account.bank}</p>
														<p>Tipo: {ucfirst(account?.type || '')}</p>
													</div>
												</div>
											))}
											{internal && transferUsdAccounts.map((account, index) => (
												<div key={index}>
													<h3 className="mb-2 text-sm font-medium text-primary-400">Deposito en Divisas</h3>
													<div className="space-y-1 text-sm text-neutral-300">
														<p>Cuenta: {account.account}</p>
														<p>Rif: {account.rif}</p>
														<p>Banco: {account.bank}</p>
														<p>Tipo: {ucfirst(account?.type || '')}</p>
													</div>
												</div>
											))}
										</div>
									</div>
								)}

								<form className="space-y-4" onSubmit={handlePaymentSubmit}>
									{country === 'colombia' && (
										<Notice
											message="Actualmente no hay métodos de pago disponibles para este país."
											variant="warning"
										/>
									)}

									{country !== 'bolivia' && country !== 'colombia' && (
										<div className="flex flex-wrap gap-4">
											<Select
												floatingLabel="Método de pago"
												options={[
													...(!internal ? [{ label: 'Pago en tienda', value: 'cash' }] : []),
													...(internal && country === 'venezuela' && transferUsdAccounts.length > 0
														? [{ label: 'Pago en Divisas', value: 'divisas' }]
														: []),
													...(country === 'venezuela' && pagoMovilAccounts.length > 0 ? [{ label: 'Pago Movil', value: 'pagomovil' }] : []),
													...(country === 'venezuela' && transferAccounts.length > 0
														? [{ label: 'Transferencia', value: 'transfer' }]
														: []),
													...(country === 'rd' && internal
														? [
																{ label: 'Transferencia', value: 'transfer_rd' },
																{ label: 'Depósito', value: 'deposito_rd' }
														  ]
														: []),
													...(country === 'rd' && !internal
														? [
																{ label: 'Transferencia a Krece', value: 'transfer_rd' },
																{ label: 'Depósito a Krece', value: 'deposito_rd' }
														  ]
														: [])
												]}
												defaultValue={payMethod}
												onSelectedChange={(value) => {
													setPayMethod(value);
													if (value) setErrorMessage((current) => current === RECEIVER_CHANGED_MESSAGE ? '' : current);
												}}
												placeholder="Seleccionar método"
											/>
											{usesCountryPaymentDetails && ['pagomovil', 'transfer'].includes(payMethod) && (
												<Select
													floatingLabel="Banco receptor"
													options={receiverOptions}
													defaultValue={selectedReceiver}
													onSelectedChange={(value) => setVerificationBank(value || 'auto')}
													placeholder="Seleccionar banco receptor"
													containerClassName="flex-grow min-w-[200px]"
												/>
											)}
											{(payMethod === 'pagomovil' || (usesCountryPaymentDetails && payMethod === 'transfer')) && (
												<Select
													floatingLabel={
														payMethod === 'pagomovil'
															? 'Banco de origen del Pago Móvil'
															: selectedReceiver === 'mercantil'
																? 'Banco emisor'
																: 'Banco emisor (opcional)'
													}
													options={banks.map((item) => ({ label: item, value: item }))}
													defaultValue={bank || undefined}
													onSelectedChange={(value) => setBank(value || '')}
													placeholder="Seleccionar banco"
													containerClassName="flex-grow min-w-[200px]"
													isDefaultValueNull={false}
												/>
											)}
											{usesCountryPaymentDetails && payMethod === 'transfer' && selectedReceiver === 'mercantil' && (
												<Input
													floatingLabel="Cédula o RIF del emisor (opcional)"
													placeholder="V12345678"
													value={issuerCustomerId}
													onChange={(value) => setIssuerCustomerId(value.trim())}
													containerClassName="flex-grow min-w-[200px]"
												/>
											)}
											{payMethod === 'cash' && (
												<Select
													floatingLabel="Local"
													options={subStoreList.map((item) => ({ label: item, value: item }))}
													defaultValue={subStore}
													onSelectedChange={(value) => setSubStore(value)}
													placeholder="Seleccionar local"
												/>
											)}
										</div>
									)}

									{country === 'bolivia' && (
										<>
											<Input
												floatingLabel="Monto a pagar"
												value={onlinePaymentAmount}
												onChange={(value) => setOnlinePaymentAmount(value)}
												allowedChars="numeric"
												prefix={CountrySettings.currency_code}
												placeholder="0.00"
												disabled={isEarlyPayment}
											/>
										</>
									)}

									{payMethod === 'cash' && (
										<>
											<Input
												floatingLabel={`Monto recibido (en ${CountrySettings.currency_name})`}
												value={amountReceived}
												onChange={(value) => setAmountReceived(value)}
												allowedChars="numeric"
												prefix={CountrySettings.currency_code}
												placeholder={`0.00`}
												disabled={isEarlyPayment}
											/>

											{storePayment2faEnabled && (
												<div className="overflow-hidden rounded-xl border border-neutral-800 bg-neutral-900/40">
													<div className="flex items-center justify-between gap-3 border-b border-neutral-800 px-4 py-3">
														<div className="flex min-w-0 items-center gap-2.5">
															<div className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-primary-500/10 ring-1 ring-inset ring-primary-500/25">
																<ShieldCheck className="size-4 text-primary-400" />
															</div>
															<div className="min-w-0">
																<h3 className="text-sm font-semibold leading-tight text-neutral-100">Verificación 2FA</h3>
																<p className="mt-0.5 text-xs leading-tight text-neutral-400">
																	{!shouldCollectStorePayment2fa
																		? 'No se requiere acción'
																		: storePayment2faAlwaysRequired
																			? 'Requerida para este monto'
																			: 'Confirma tu identidad para procesar el pago'}
																</p>
															</div>
														</div>
														{!shouldCollectStorePayment2fa ? (
															<span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-emerald-500/10 px-2 py-1 text-[11px] font-medium text-emerald-400 ring-1 ring-inset ring-emerald-500/20">
																<CircleCheck className="size-3" />
																Activa
															</span>
														) : storePayment2faAlwaysRequired ? (
															<span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-amber-500/10 px-2 py-1 text-[11px] font-medium text-amber-400 ring-1 ring-inset ring-amber-500/20">
																Requerida
															</span>
														) : null}
													</div>

													{(shouldCollectStorePayment2fa || showStore2faPhoneEditor) && (
														<div className="space-y-3 px-4 py-3">
															{showStore2faPhoneEditor ? (
															<div className="space-y-3 rounded-lg bg-neutral-1000/40 p-3 ring-1 ring-inset ring-neutral-800">
																<div className="flex items-center justify-between">
																	<span className="text-xs font-semibold text-neutral-200">
																		{masterStorePayment2fa?.['2fa_phone_number']
																			? 'Cambiar número de teléfono'
																			: 'Configurar número de teléfono'}
																	</span>
																	<button
																		type="button"
																		onClick={() => setShowStore2faPhoneEditor(false)}
																		className="text-[11px] text-neutral-400 transition-colors hover:text-neutral-100"
																	>
																		Cerrar
																	</button>
																</div>
																<p className="text-[11px] text-neutral-400">
																	Por seguridad, recibirás un código en el email master para confirmar el cambio.
																</p>
																<PhoneInput country={country} value={store2faPhoneValue} onChange={setStore2faPhoneValue} />

																<TwoFactorAuthV2
																	variant="compact"
																	methods={[
																		{
																			type: 'email',
																			email: masterStorePayment2fa?.email || userData?.master_store?.id,
																			allowEmailInput: false,
																			label: 'Enviar código al email master'
																		}
																	]}
																	country={country || ''}
																	confirmCode={false}
																	onCodeSent={() => {
																		setStore2faPhoneCodeSent(true);
																		setStore2faPhoneMessage('Código enviado al email master.');
																		setStore2faPhoneError('');
																	}}
																/>

																{store2faPhoneCodeSent && (
																	<Input
																		floatingLabel="Código del email master"
																		value={store2faPhoneCode}
																		onChange={setStore2faPhoneCode}
																		allowedChars="numbers_only"
																		maxLength={6}
																		placeholder="000000"
																		Icon={<KeyRound className="size-4 text-primary-500" />}
																	/>
																)}

																<div className="flex flex-wrap gap-2 pt-0.5">
																	<Button
																		type="button"
																		variant="primary"
																		loading={store2faPhoneLoading && store2faPhoneCodeSent}
																		disabled={store2faPhoneLoading}
																		onClick={saveStore2faPhoneNumber}
																		className="!h-9 !w-auto !py-0 !text-xs"
																	>
																		Guardar número
																	</Button>
																	<Button
																		type="button"
																		variant="text"
																		disabled={store2faPhoneLoading}
																		onClick={() => setShowStore2faPhoneEditor(false)}
																		className="!h-9 !w-auto !py-0 !text-xs"
																	>
																		Cancelar
																	</Button>
																</div>
																{store2faPhoneMessage && <Notice message={store2faPhoneMessage} variant="success" />}
																{store2faPhoneError && <Notice message={store2faPhoneError} variant="error" />}
															</div>
														) : (
															<>
																<p className="text-xs text-neutral-400">¿Cómo quieres recibir el código?</p>
																<TwoFactorAuthV2
																	variant="compact"
																	methods={storePayment2faMethods}
																	country={country || ''}
																	confirmCode={false}
																	onCodeSent={(method, destination) => {
																		setStorePayment2faSelectedMethod({ type: method, destination });
																		setStorePayment2faCodeSent(true);
																		setStorePayment2faCodeInput('');
																		setStorePayment2fa(null);
																		setErrorMessage('');
																	}}
																/>

																{storePayment2faCodeSent && (
																	<Input
																		floatingLabel="Código 2FA"
																		value={storePayment2faCodeInput}
																		onChange={setStorePayment2faCode}
																		allowedChars="numbers_only"
																		maxLength={6}
																		placeholder="000000"
																		Icon={<KeyRound className="size-4 text-primary-500" />}
																	/>
																)}

																{storePayment2fa?.code && (
																	<div className="flex items-center gap-2 rounded-md bg-emerald-500/10 px-3 py-2 text-xs text-emerald-300 ring-1 ring-inset ring-emerald-500/20">
																		<CircleCheck className="size-3.5 shrink-0" />
																		Código listo para validar al procesar el pago
																	</div>
																)}

																{userData?.master_user && (
																	<div className="flex justify-end pt-0.5">
																		<button
																			type="button"
																			onClick={openStore2faPhoneEditor}
																			className="inline-flex items-center gap-1.5 text-[11px] font-medium text-neutral-400 transition-colors hover:text-primary-400"
																		>
																			<Edit className="size-3" />
																			{masterStorePayment2fa?.['2fa_phone_number']
																				? 'Cambiar número de teléfono'
																				: 'Agregar número de teléfono'}
																		</button>
																	</div>
																)}
															</>
														)}
													</div>
												)}
												</div>
											)}
										</>
									)}

									{payMethod === 'transfer_rd' && (
										<>
											<Input
												floatingLabel="Monto a pagar"
												value={amountToPayDollars?.toString() || ''}
												onChange={(value) => setAmountToPayDollars(parseFloat(value || 0) || 0)}
												placeholder="Ingrese el monto a pagar"
												allowedChars="numeric"
												disabled={isEarlyPayment}
											/>
											<Input
												floatingLabel="Últimos 4 dígitos de la cuenta bancaria de origen"
												value={accountNumber}
												onChange={(value) => setAccountNumber(value.trim())}
												placeholder="Ingrese los últimos 4 dígitos"
												allowedChars="numbers_only"
												maxLength={4}
											/>
											<Input
												floatingLabel="Nombre del titular de la cuenta bancaria de origen"
												value={accountHolder}
												onChange={(value) => setAccountHolder(value.trim())}
												placeholder="Ingrese el nombre del titular"
											/>

											<DatePicker floatingLabel="Fecha del pago" onDateChange={(date) => setPaymentDate(date)} />

											<div className="space-y-2">
												<label className="block text-sm font-medium text-neutral-300">Foto de comprobante</label>
												<input
													type="file"
													accept=".jpg,.jpeg,.png,.tiff,.webp,.bmp"
													onChange={handleFileUpload}
													className="hidden"
													id="payment-proof-upload"
												/>
												<label
													htmlFor="payment-proof-upload"
													className={classNames(
														'hover:bg-neutral-750 flex cursor-pointer items-center gap-2 rounded-lg border border-neutral-700 bg-neutral-800 px-4 py-2.5 text-sm transition-colors duration-200 light:bg-neutral-900',
														uploadingProof && 'cursor-not-allowed opacity-50'
													)}
												>
													{uploadingProof ? (
														<>
															<Spinner size={1} />
															<span>Subiendo documento...</span>
														</>
													) : (
														<>
															{paymentProof ? (
																<>
																	<CheckCircle className="size-4 text-green-500" />
																	<span>Documento subido exitosamente</span>
																</>
															) : (
																<>
																	<Upload className="size-4" />
																	{paymentProof ? 'Volver a subir' : 'Subir imagen de comprobante'}
																</>
															)}
														</>
													)}
												</label>
												<span className="text-xs text-neutral-400">JPG, PNG, WEBP (máx. 5MB)</span>
												{paymentProof && (
													<div className="mt-2">
														<img src={paymentProof} alt="Comprobante" className="h-32 w-auto rounded-md object-contain" />
													</div>
												)}
											</div>
										</>
									)}

									{payMethod === 'deposito_rd' && (
										<>
											<Input
												floatingLabel="Monto a pagar"
												value={amountToPayDollars?.toString() || ''}
												onChange={(value) => setAmountToPayDollars(parseFloat(value || 0) || 0)}
												placeholder="Ingrese el monto a pagar"
												allowedChars="numeric"
												disabled={isEarlyPayment}
											/>

											<DatePicker floatingLabel="Fecha del pago" onDateChange={(date) => setPaymentDate(date)} />

											<div className="space-y-2">
												<label className="block text-sm font-medium text-neutral-300">Foto de comprobante</label>
												<input
													type="file"
													accept=".jpg,.jpeg,.png,.tiff,.webp,.bmp"
													onChange={handleFileUpload}
													className="hidden"
													id="payment-proof-upload"
												/>
												<label
													htmlFor="payment-proof-upload"
													className={classNames(
														'hover:bg-neutral-750 flex cursor-pointer items-center gap-2 rounded-lg border border-neutral-700 bg-neutral-800 px-4 py-2.5 text-sm transition-colors duration-200 light:bg-neutral-900',
														uploadingProof && 'cursor-not-allowed opacity-50'
													)}
												>
													{uploadingProof ? (
														<>
															<Spinner size={1} />
															<span>Subiendo documento...</span>
														</>
													) : (
														<>
															{paymentProof ? (
																<>
																	<CheckCircle className="size-4 text-green-500" />
																	<span>Documento subido exitosamente</span>
																</>
															) : (
																<>
																	<Upload className="size-4" />
																	{paymentProof ? 'Volver a subir' : 'Subir imagen de comprobante'}
																</>
															)}
														</>
													)}
												</label>
												<span className="text-xs text-neutral-400">JPG, PNG, WEBP (máx. 5MB)</span>
												{paymentProof && (
													<div className="mt-2">
														<img src={paymentProof} alt="Comprobante" className="h-32 w-auto rounded-md object-contain" />
													</div>
												)}
											</div>
										</>
									)}

									{payMethod !== 'cash' && payMethod !== 'transfer_rd' && payMethod && country === 'venezuela' && (
										<>
											<Input
												floatingLabel="Número de referencia"
												value={confirmationNumber}
												onChange={(value) => setConfirmationNumber(value.trim())}
												placeholder="Ingrese el número de referencia"
											/>
											<Input
												floatingLabel={`Monto del pago (en ${payMethod === 'divisas' ? 'dólares' : 'bolívares'})`}
												value={amountPaid}
												onChange={(value) => setAmountPaid(value.trim())}
												allowedChars="numeric"
												placeholder={`0.00`}
												disabled={isEarlyPayment}
											/>
											{payMethod === 'pagomovil' && (
												<Input
													type="tel"
													floatingLabel={usesCountryPaymentDetails && selectedReceiver === 'r4' ? 'Teléfono de origen (opcional)' : 'Teléfono de origen'}
													value={phoneNumber}
													onChange={(value) => setPhoneNumber(value.trim())}
													placeholder="Ejemplo: 04141234567"
													allowedChars="numbers_only"
												/>
											)}

											<DatePicker floatingLabel="Fecha del pago" onDateChange={(date) => setPaymentDate(date)} />
										</>
									)}

									{errorMessage && <Notice message={errorMessage} variant="error" />}
									{prontoPagoNotice?.message && prontoPagoNotice?.type && (
										<Notice message={prontoPagoNotice.message} variant={prontoPagoNotice.type} />
									)}

									<div className="flex flex-wrap gap-2 md:flex-row-reverse md:flex-nowrap">
										{country !== 'colombia' && (
											<Button
												onClick={handlePaymentSubmit}
												loading={isLoading}
												disabled={isLoading}
												variant="primary"
												type="submit"
											>
												{country === 'bolivia' ? 'Pagar online' : 'Enviar'}
											</Button>
										)}

										<Button
											disabled={isLoading}
											onClick={() => {
												clearPreparedPayment();
												setErrorMessage('');
												setProntoPagoNotice({});
												setProntoPagoAmount(null);
												setLoadingProntoPago({});
												setAmountPaid('');
												setAmountReceived('');
												setOnlinePaymentAmount('');
											}}
											Icon={<ArrowLeft className="size-5" />}
											variant="secondary"
										>
											Atrás
										</Button>
									</div>
								</form>
							</div>
						</>
					)}

					{page === 4 && (
						<>
							{render_page_header('Pago Exitoso', '¡El pago se ha completado correctamente!', <CircleCheck />)}

							{storePaymentReceipt && (
								<div className="mb-4 space-y-4">
									<div className="rounded-xl border border-neutral-700/50 bg-neutral-800/50 p-4 light:bg-neutral-1000">
										<div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
											<div>
												<p className="text-sm text-neutral-400">Pago recibido en tienda</p>
												<p className="mt-1 text-2xl font-semibold text-primary-400 light:text-primary-700">
													{storePaymentReceipt.currencyCode}
													{formatNumber(storePaymentReceipt.amount)}
												</p>
											</div>
											<span className="text-left text-xs text-neutral-400 sm:text-right">
												Confirmación
												<strong className="mt-0.5 block break-all text-sm text-neutral-100">
													{storePaymentReceipt.confirmationCode}
												</strong>
											</span>
										</div>
										<div className="mt-4 border-t border-neutral-700/50 pt-3 text-sm text-neutral-300">
											<p className="truncate" title={storePaymentReceipt.clientName}>
												{storePaymentReceipt.clientName}
											</p>
											<p className="mt-1 text-neutral-400">{formatReceiptDate(storePaymentReceipt.paidAt)}</p>
										</div>
									</div>
									<Button Icon={<Printer className="size-5" />} onClick={printStorePaymentReceipt} variant="primary">
										Imprimir o guardar PDF
									</Button>
								</div>
							)}

							<div className="flex gap-2">
								<Button
									Icon={<ArrowLeft className="size-5" />}
									onClick={() => {
										setSuccess(false);
										setStorePaymentReceipt(null);
										setErrorMessage('');
										setPaymentDocId('');
										setDialogMessage('');
										setShowDialog(false);
										setShowConfirmDialog(false);
										setPendingConfirmAction(null);
										setProntoPagoAmount(null);
										setProntoPagoNotice({});
										setLoadingProntoPago({});
										setOnlinePaymentAmount('');
									}}
									variant={storePaymentReceipt ? 'secondary' : 'primary'}
								>
									Realizar otro pago
								</Button>
								<Button
									onClick={() => {
										resetAll();
									}}
									variant="secondary"
								>
									Buscar otro cliente
								</Button>
							</div>
						</>
					)}
				</div>
			</div>

			{showDialog && <Dialog onClose={() => setShowDialog(false)}>{dialogMessage}</Dialog>}

			{showConfirmDialog && (
				<Dialog closable={false} title="Confirmar" onClose={() => setShowConfirmDialog(false)}>
					<div className="flex flex-col gap-4">
						<div dangerouslySetInnerHTML={{ __html: dialogMessage }} />
						<div className="flex justify-end gap-2">
							<button
								onClick={() => setShowConfirmDialog(false)}
								className="inline-flex h-[35px] items-center justify-center rounded-lg px-[15px] font-medium leading-none text-neutral-600 transition-colors hover:bg-neutral-900/50 focus:outline-none disabled:pointer-events-none disabled:opacity-50"
							>
								Cancelar
							</button>
							<button
								onClick={() => {
									setShowConfirmDialog(false);
									pendingConfirmAction();
								}}
								className="inline-flex h-[35px] items-center justify-center rounded-lg bg-primary-500 px-[15px] font-medium leading-none text-black transition-colors hover:bg-primary-600 focus:outline-none disabled:pointer-events-none disabled:opacity-50"
							>
								Confirmar
							</button>
						</div>
					</div>
				</Dialog>
			)}
		</div>
	);

	function render_page_header(title, description, icon) {
		return (
			<div className="mb-4 flex flex-col items-center gap-2 md:mb-6 md:gap-3">
				<div className="relative">
					<div className="absolute -inset-1 rounded-full bg-gradient-to-r from-primary-400 to-primary-600 opacity-10 blur"></div>
					<div className="relative rounded-full bg-neutral-900 p-4 light:bg-neutral-1000 light:ring-2 light:ring-primary-500/10">
						{icon && React.cloneElement(icon, { className: 'h-6 w-6 text-primary-500' })}
					</div>
				</div>
				<div className="space-y-1 text-center">
					<h1 className="text-2xl font-semibold tracking-tighter text-neutral-0 md:text-3xl">{title}</h1>
					{description && <p className="text-neutral-300">{description}</p>}
				</div>
			</div>
		);
	}
};

export default Pay;
